// suspension.js
// Encapsulates presets, support functions, and UI wiring for suspension controls

import { VEHICLE_TYPE_RULES, SUSPENSION_PRESETS } from '../../common/srt/srt-common-presets.js';
import $ from 'jquery';

// Field keys for iteration
export const SUSPENSION_FIELDS = ['spring-spacing', 'lash', 'roll-stiffness', 'spring-stiffness', 'roll-center-height'];

// Fetch allowed keys from rules
function getAllowedSuspensionKeys(vehicleType, group) {
   return VEHICLE_TYPE_RULES[vehicleType]?.[group]?.allowedSuspension || [];
}

// Get default suspension type for a vehicle type and group
function getDefaultSuspension(vehicleType, group) {
   return VEHICLE_TYPE_RULES[vehicleType]?.[group]?.defaultSuspension || [];
}

// Populate suspension <select> with allowed presets
export function populateSuspensionSelect(vehicleType, group, unitIndex) {
   const keys = getAllowedSuspensionKeys(vehicleType, group);
   const defaultKey = getDefaultSuspension(vehicleType, group);
   const $sel = $(`#suspension-type-${group}-${unitIndex}`).empty();
   keys.forEach((key) => {
      const label = SUSPENSION_PRESETS[key]?.label;
      if (label) $sel.append(`<option value="${key}">${label}</option>`);
   });
   // set default value and trigger change
   if (defaultKey && keys.includes(defaultKey)) {
      $sel.val(defaultKey).trigger('change');
   }
}

// Validate numeric input
function ValidateSuspensionField($el) {
   const num = parseFloat($el.val());
   $el.toggleClass('is-invalid', isNaN(num));
}

// Apply preset values/read-only styling
export function applyPreset(group, unitIndex) {
   const selKey = $(`#suspension-type-${group}-${unitIndex}`).val();
   const preset = SUSPENSION_PRESETS[selKey];
   if (!preset) return; // No preset for user-defined or unknown to be applied

   const values = preset.values;
   const readOnly = !preset.editable;
   const prefix = group === 'front' ? 'f' : 'r';

   SUSPENSION_FIELDS.forEach((fieldKey) => {
      const camel = fieldKey.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      const $el = $(`#${prefix}-${fieldKey}-${unitIndex}`);
      let val = values[camel];

      // convert lash from m to mm
      if (fieldKey === 'lash' && val != null) val = val * 1000;

      // set read-only state and class
      $el.prop('readonly', readOnly).toggleClass('bg-light text-muted', readOnly);

      if (readOnly) {
         $el.val(val ?? '')
            .removeClass('is-invalid')
            .off('input.suspension');
      } else {
         if (val != null) $el.val(val);
         ValidateSuspensionField($el);
         $el.off('input.suspension').on('input.suspension', () => ValidateSuspensionField($el));
      }
   });

   // also update calculated auxiliary stiffness
   updateAuxStiffnessDisplay(prefix + '-', unitIndex, 100);
}

// Refresh selects & presets for one unit based on vehicle type
export function updateSuspensionOptions(vt, unitIndex) {
   ['front', 'rear'].forEach((group) => {
      populateSuspensionSelect(vt, group, unitIndex);
      applyPreset(group, unitIndex);
   });
}

// Initialize UI handlers
export function initSuspensionModule(unitIdx) {
   // hide advanced fields by default
   $('.advanced-fields').hide();

   // suspension-type change => reapply values/style
   $('.suspension-type-select').on('change', function () {
      const [, , group, idx] = this.id.split('-');
      applyPreset(group, Number(idx));
      // if user Defined selected, also show advanced fields
      if ($(this).val() === 'user') {
         $(`#show-advanced-${idx}`).prop('checked', true).trigger('change');
      }
   });

   // shared advanced toggle => show/hide both front & rear
   $('.show-advanced-toggle').prop('checked', false);
   $('.show-advanced-toggle').on('change', function () {
      const idx = this.id.split('-')[2];
      const checked = this.checked;
      $(`#collapse-suspension-${idx} .advanced-fields`).toggle(checked);
   });

   // seed all selects by scanning DOM
   $('.suspension-type-select').each(function () {
      const [, , group, idx] = this.id.split('-'); //['suspension', 'type', 'front', '0']
      updateSuspensionOptions(Number(idx));
   });

   // bind handlers for auxiliary stiffness calculations
   bindSuspensionAuxHandlers('f-', unitIdx);
   bindSuspensionAuxHandlers('r-', unitIdx);
}

const AUX_DEG_FACTOR = 57.29577951308232; // Nm/rad → Nm/deg = /57.2958

/**
 * Bind change handlers for a given side/unitIdx.
 * Call once for each group after DOM created.
 */
export function bindSuspensionAuxHandlers(prefix, unitIdx) {
   // Relevant inputs to observe
   const ids = [
      `#${prefix}spring-spacing-${unitIdx}`,
      `#${prefix}spring-stiffness-${unitIdx}`,
      `#${prefix}roll-stiffness-${unitIdx}`,
   ].join(', ');

   // Use direct .on binding per your style guide (no delegation unless needed)
   $(ids).on('input change', () => {
      // Recompute on every change
      updateAuxStiffnessDisplay(prefix, unitIdx, 100);
   });

   // compute once on bind in case initial values exist
   updateAuxStiffnessDisplay(prefix, unitIdx, 100);
}

/**
 * Compute auxiliary roll stiffness and update DOM
 * @param {object} opts
 * @param {string} opts.prefix - e.g. 'f-' or 'r-'
 * @param {number} opts.unitIndex
 * @param {number} [roundToRad=100] round auxiliary Nm/rad for display
 */
function updateAuxStiffnessDisplay(prefix, unitIndex, roundToRad = 100) {
   // Build selectors from prefix / unitIndex
   const sid = (id) => `#${prefix}${id}-${unitIndex}`;

   // Read values (guard for empty)
   const parse = (sel) => {
      const v = $(sel).val();
      if (v === '' || v == null) return NaN;
      return Number.parseFloat(v);
   };

   const springSpacing = parse(sid('spring-spacing'));
   const springStiffness = parse(sid('spring-stiffness'));
   const totalRoll = parse(sid('roll-stiffness')); // per spring in preset

   // Compute spring roll contribution per axle: (per spring) × (spacing² / 2)
   const springRoll =
      Number.isFinite(springStiffness) && Number.isFinite(springSpacing)
         ? springStiffness * (springSpacing ** 2 / 2)
         : NaN;

   // Compute aux = per-axle total roll − per-axle spring roll
   let auxRad = Number.isFinite(totalRoll) && Number.isFinite(springRoll) ? totalRoll - springRoll : NaN;

   // display elements
   const $auxRad = $(`#${prefix}aux-stiffness-${unitIndex}-rad`);
   const $auxDeg = $(`#${prefix}aux-stiffness-${unitIndex}-deg`);
   const $warn = $(`#${prefix}aux-warning-${unitIndex}`);

   // clear warning by default
   $warn.addClass('d-none');

   if (!Number.isFinite(auxRad)) {
      // Insufficient inputs — blank the displays
      $auxRad.val('—');
      $auxDeg.val('—');
      return;
   }

   // Round auxRad for display
   if (auxRad < 0 && auxRad > -1000) {
      auxRad = 0; // treat small negative as zero after rounding
   }
   const roundedAuxRad = Math.round(auxRad / roundToRad) * roundToRad;

   // Compute deg equivalent (use unrounded rad -> deg, then round for clarity)
   const auxDeg = auxRad / AUX_DEG_FACTOR;
   const roundedAuxDeg = Math.round(auxDeg / 100) * 100;

   // Update displays
   $auxRad.val(roundedAuxRad.toLocaleString());
   $auxDeg.val(roundedAuxDeg.toLocaleString());

   // If auxRad is negative — show warning and red highlight
   if (auxRad < 0) {
      $warn.removeClass('d-none');
      $auxRad.addClass('is-invalid');
      $auxDeg.addClass('is-invalid');
      console.warn(`[suspension] aux stiffness negative for ${prefix}${unitIndex}: ${auxRad}`);
   } else {
      // valid
      $auxRad.removeClass('is-invalid');
      $auxDeg.removeClass('is-invalid');
   }
}

/**
 * Update the displayed track width in the form.
 * @param {string} prefix - Field prefix for this unit (e.g., 'f-' or '').
 * @param {number} unitIndex - Unit index number.
 * @param {number|string} value - Track width in metres or millimetres.
 */
export function updateTrackWidthDisplay(prefix, unitIndex, value) {
   const $field = $(`#${prefix}track-width-${unitIndex}`);

   if (!$field.length) {
      console.warn(`⚠ Track width field not found for prefix "${prefix}" and unitIndex ${unitIndex}`);
      return;
   }

   // Format the value or show placeholder
   const displayValue = value != null && !isNaN(value) && value > 0 ? Number(value.toFixed(2)) : '—';

   $field.val(displayValue);
}
